import { AuthError, confirmResetPassword } from "aws-amplify/auth";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordRules } from "@/features/registration/components/password-rules";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  NewPasswordFormData,
  newPasswordSchema,
} from "../types/new-password.schema";

export function NewPasswordForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const form = useForm<NewPasswordFormData>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: {
      password: "",
    },
  });

  async function onSubmit(values: NewPasswordFormData) {
    try {
      const confirmationCode = searchParams.get("code");
      if (!confirmationCode) {
        form.setError("root", {
          message:
            "Confirmation code is missing. Please check your reset password link.",
        });
        return;
      }

      const email = searchParams.get("email")?.toLowerCase().trim();
      if (!email) {
        form.setError("root", {
          message: "Email is missing. Please check your reset password link.",
        });
        return;
      }

      await confirmResetPassword({
        username: email,
        confirmationCode: confirmationCode,
        newPassword: values.password,
      });

      navigate("/sign-in");
    } catch (error) {
      if (error instanceof AuthError) {
        switch (error.name) {
          case "CodeMismatchException":
            form.setError("root", {
              message: "Invalid confirmation code. Please try again.",
            });
            break;
          case "ExpiredCodeException":
            form.setError("root", {
              message:
                "Confirmation code has expired. Please request a new one.",
            });
            break;
          case "LimitExceededException":
            form.setError("root", {
              message: "Attempt limit exceeded. Please try again later.",
            });
            break;
          default:
            form.setError("root", {
              message:
                "An error occurred during password reset. Please try again.",
            });
        }
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel isRequired>New Password</FormLabel>
                <FormControl>
                  <div className="relative flex">
                    <Input
                      type={isPasswordVisible ? "text" : "password"}
                      placeholder="••••••"
                      {...field}
                      className="pr-8"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      type="button"
                      className="absolute right-0 top-1/2 -translate-y-1/2 hover:bg-transparent"
                      onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                    >
                      {isPasswordVisible ? (
                        <EyeOff className="size-4" aria-label="Hide password" />
                      ) : (
                        <Eye className="size-4" aria-label="Show password" />
                      )}
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <PasswordRules
            password={form.watch("password")}
            schema={newPasswordSchema}
          />
        </div>
        {form.formState.errors.root && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unexpected error</AlertTitle>
            <AlertDescription>
              {form.formState.errors.root.message}
            </AlertDescription>
          </Alert>
        )}
        <Button
          type="submit"
          className="w-full"
          disabled={!form.formState.isValid}
          isLoading={form.formState.isSubmitting}
          isAnimated
        >
          Set New Password
        </Button>
      </form>
    </Form>
  );
}
